import React, { useCallback, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  BioLinkAutoAddPosition,
  BioLinkKeepSongs,
  BioLinkSettings,
  DEFAULT_BIO_LINK_SETTINGS,
} from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { useGetBioLinkSettingsQuery, useUpdateBioLinkSettingsMutation } from '../../../../redux/api/fymAPI';
import { SMLSwitch } from '../controls/SMLSwitch';
import { SMLSegmented, SegmentedOption } from '../controls/SMLSegmented';
import { useEditorContext } from '../SMLEditorContext';
import { pageMessages as m } from './pageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLPageTab.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The select needs a string; null (keep all) is "all". */
type KeepValue = '1' | '3' | '5' | 'all';

function keepValueOf(keep: BioLinkKeepSongs): KeepValue {
  return keep === null ? 'all' : (String(keep) as KeepValue);
}

function keepSongsOf(value: string): BioLinkKeepSongs {
  if (value === '1' || value === '3' || value === '5') {
    return Number(value) as 1 | 3 | 5;
  }
  return null;
}

/**
 * "New songs" on the Bio Link (design e4): add new songs automatically (on by default), where they
 * go, and how many older song blocks stay shown. Each change saves at once.
 */
const SMLBioLinkSettingsCardComponent: React.FC = () => {
  const intl = useIntl();
  const keepId = useId();
  const { accountId, saves } = useEditorContext();
  const { data, isLoading, error, refetch } = useGetBioLinkSettingsQuery({ accountId });
  const [update, { isLoading: isSaving }] = useUpdateBioLinkSettingsMutation();
  const settings: BioLinkSettings = data?.settings ?? DEFAULT_BIO_LINK_SETTINGS;

  const change = useCallback(
    (patch: Partial<BioLinkSettings>) => {
      saves.track(update({ accountId, ...patch }).unwrap()).catch(console.error);
    },
    [accountId, saves, update]
  );
  const onAutoAdd = useCallback((autoAddSongs: boolean) => change({ autoAddSongs }), [change]);
  const onWhere = useCallback((position: BioLinkAutoAddPosition) => change({ position }), [change]);
  const onKeep = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => change({ keepSongs: keepSongsOf(event.target.value) }),
    [change]
  );
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const whereOptions = useMemo<SegmentedOption<BioLinkAutoAddPosition>[]>(
    () => [
      { value: 'afterProfile', label: intl.formatMessage(m.whereProfile) },
      { value: 'end', label: intl.formatMessage(m.whereEnd) },
    ],
    [intl]
  );

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }

  return (
    <section className={styles.card} data-testid="bio-link-settings">
      <h4 className={styles.cardTitle}>{intl.formatMessage(m.newSongs)}</h4>
      <div className={styles.statusRow}>
        <span className={styles.statusText}>
          <b>{intl.formatMessage(m.autoAdd)}</b>
          <small>{intl.formatMessage(m.autoAddHelp)}</small>
        </span>
        <SMLSwitch
          checked={settings.autoAddSongs}
          onChange={onAutoAdd}
          disabled={isSaving}
          ariaLabel={intl.formatMessage(m.autoAdd)}
          testId="bio-auto-add"
        />
      </div>
      {settings.autoAddSongs && (
        <>
          <div className={styles.field}>
            <span className={styles.label}>{intl.formatMessage(m.where)}</span>
            <SMLSegmented
              options={whereOptions}
              value={settings.position}
              onChange={onWhere}
              disabled={isSaving}
              ariaLabel={intl.formatMessage(m.where)}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={keepId} className={styles.label}>
              {intl.formatMessage(m.olderSongs)}
            </label>
            <select
              id={keepId}
              className={`${styles.input} ${styles.inputBox}`}
              value={keepValueOf(settings.keepSongs)}
              onChange={onKeep}
              disabled={isSaving}
            >
              <option value="1">{intl.formatMessage(m.keepOne)}</option>
              <option value="3">{intl.formatMessage(m.keepThree)}</option>
              <option value="5">{intl.formatMessage(m.keepFive)}</option>
              <option value="all">{intl.formatMessage(m.keepAll)}</option>
            </select>
            <p className={styles.help}>
              {intl.formatMessage(settings.keepSongs === null ? m.keepAllHelp : m.keepHelp)}
            </p>
          </div>
        </>
      )}
      <p className={styles.note}>{intl.formatMessage(m.newSongsNote)}</p>
    </section>
  );
};

export const SMLBioLinkSettingsCard = TypedMemo(SMLBioLinkSettingsCardComponent);
